import {generateDefaultModel} from '@github-ui/copilot-chat/utils/models'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {sendEvent} from '@github-ui/hydro-analytics'
import {useSearchParams} from '@github-ui/use-navigate'
import type React from 'react'
import {useEffect, useRef, useState} from 'react'

import {ChatContent} from '../components/ChatContent'
import {ChatContentV2} from '../components/ChatContentV2/ChatContentV2'
import {CopilotHeader} from '../components/CopilotHeader'
import {CopilotContext} from '../contexts/CopilotContext'
import {useInitialCopilotContext} from '../hooks/useInitialCopilotContext'

const CopilotImmersiveLoggedOut: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const defaultModel = generateDefaultModel()
  const [initialModelID, setInitialModelID] = useState<string | null>(null)
  const autoPopulated = useRef(false)

  const {copilotContextValue} = useInitialCopilotContext({defaultModel})
  const isHyperspace2025LoggedOutBatch1 = isFeatureEnabled('hyperspace_2025_logged_out_batch_1')

  useEffect(() => {
    if (autoPopulated.current) return
    autoPopulated.current = true

    const queryPrompt = searchParams.get('prompt')
    if (queryPrompt) copilotContextValue.setPrompt(queryPrompt)

    const queryModelID = searchParams.get('model')
    setInitialModelID(queryModelID)

    const newSearchParams = new URLSearchParams(searchParams)
    newSearchParams.delete('prompt')
    newSearchParams.delete('model')
    setSearchParams(newSearchParams, {replace: true})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, setSearchParams])

  const ChatContentComponent = isHyperspace2025LoggedOutBatch1 ? ChatContentV2 : ChatContent

  return (
    <>
      <CopilotHeader />
      <CopilotContext value={copilotContextValue}>
        <ChatContentComponent
          initialModelID={initialModelID}
          onIdeListItemClick={(_, ideName) => {
            sendEvent('click', {
              category: 'copilot_loggedOut',
              action: 'ide_option_click',
              label: 'copilot_loggedOut',
              ideName,
            })
          }}
          onProductAnnouncementBannerEvent={event => {
            switch (event) {
              case 'displayed':
                sendEvent('view', {
                  category: 'universe_banner',
                  action: 'banner_view',
                  label: 'banner_universe_loggedOut',
                })
                break
              case 'clicked':
                sendEvent('click', {
                  category: 'universe_banner',
                  action: 'banner_click_openModal',
                  label: 'banner_universe_loggedOut',
                })
                break
              case 'dismissed':
                sendEvent('click', {
                  category: 'universe_banner',
                  action: 'banner_dismiss',
                  label: 'banner_universe_loggedOut',
                })
                break
            }
          }}
          onProductAnnouncementModalEvent={(event, data) => {
            switch (event) {
              case 'opened':
                sendEvent('view', {
                  category: 'universe_modal',
                  action: 'modal_view',
                  label: 'modal_universe_loggedOut',
                })
                break
              case 'closed':
                sendEvent('click', {
                  category: 'universe_modal',
                  action: 'modal_close',
                  label: 'modal_universe_loggedOut',
                })
                break
              case 'tab_clicked':
                sendEvent('click', {
                  category: 'universe_modal',
                  action: 'modal_nav_click',
                  label: 'modal_universe_loggedOut',
                  navItem: data?.tabName as string,
                })
                break
              case 'main_cta_clicked':
                sendEvent('click', {
                  category: 'universe_modal',
                  action: 'modal_primary_cta_left_click',
                  label: 'modal_universe_loggedOut',
                })
                break
              case 'see_all_clicked':
                sendEvent('click', {
                  category: 'universe_modal',
                  action: 'modal_seeAllAnnouncements_left_click',
                  label: 'modal_universe_loggedOut',
                })
                break
              case 'tab_cta_1_clicked':
                sendEvent('click', {
                  category: 'universe_modal',
                  action: 'modal_primary_cta_right_click',
                  label: 'modal_universe_loggedOut',
                  navItem: data?.tabName as string,
                  ctaType: 'primary_right',
                  ctaUrl: data?.url as string,
                  userStatus: 'logged_out',
                  positionIndex: data?.tabIndex as number,
                })
                break
              case 'tab_cta_2_clicked':
                sendEvent('click', {
                  category: 'universe_modal',
                  action: 'modal_secondary_cta_right_click',
                  label: 'modal_universe_loggedOut',
                  navItem: data?.tabName as string,
                  ctaType: 'secondary_right',
                  ctaUrl: data?.url as string,
                  userStatus: 'logged_out',
                  positionIndex: data?.tabIndex as number,
                })
                break
              case 'tab_link_clicked':
                sendEvent('click', {
                  category: 'universe_modal',
                  action: 'modal_link_right_click',
                  label: 'modal_universe_loggedOut',
                  navItem: data?.tabName as string,
                  ctaType: 'learn_more_right',
                  ctaUrl: data?.url as string,
                  userStatus: 'logged_out',
                  positionIndex: data?.tabIndex as number,
                })
                break
              case 'tab_description_link_clicked':
                sendEvent('click', {
                  category: 'universe_modal',
                  action: 'modal_description_link_right_click',
                  label: 'modal_universe_loggedOut',
                  navItem: data?.tabName as string,
                  ctaType: 'link',
                  ctaUrl: data?.url as string,
                  userStatus: 'logged_out',
                  positionIndex: data?.tabIndex as number,
                })
                break
            }
          }}
        />
      </CopilotContext>
    </>
  )
}

export default CopilotImmersiveLoggedOut

try{ CopilotImmersiveLoggedOut.displayName ||= 'CopilotImmersiveLoggedOut' } catch {}